import { createTranslator, isLanguage, pickLanguage, type Language } from '@smarton-pbx/shared';
import { Fragment, type ReactNode } from 'react';
import { lt } from './lt';

/**
 * The language this page shows: chosen once, when the app loads, so labels
 * kept in constants translate too. Switching saves the choice and loads the
 * page again in it.
 */
const STORAGE_KEY = 'language';

function stored(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

function startLanguage(): Language {
  const saved = stored();
  return isLanguage(saved) ? saved : pickLanguage(navigator.languages);
}

export const language: Language = startLanguage();
export const { t, tp, tc, message } = createTranslator(language, language === 'lt' ? lt : {});

/** For dates, times and numbers: Lithuanian ones in Lithuanian; in English, British ones (the 24-hour clock, day before month). */
export const locale: string = language === 'lt' ? 'lt-LT' : 'en-GB';

document.documentElement.lang = language;

/** Shows the app in this language from now on (in this browser), loading the page again. */
export function showLanguage(next: Language): void {
  try {
    localStorage.setItem(STORAGE_KEY, next);
  } catch {
    // Private window: only until the page is loaded again.
  }
  // Without storage the page would load in the old language again: don't loop.
  if (next !== language && stored() === next) window.location.reload();
}

/**
 * A sentence with parts that are elements (a link, bold text): `rich('Read
 * the {guide} first.', { guide: <a href="…">{t('guide')}</a> })`. The
 * translation can put each part where its language needs it.
 */
export function rich(text: string, parts: Record<string, ReactNode>): ReactNode[] {
  return t(text)
    .split(/(\{\w+\})/)
    .filter(Boolean)
    .map((piece, i) => {
      const name = /^\{(\w+)\}$/.exec(piece)?.[1];
      return name && name in parts ? <Fragment key={i}>{parts[name]}</Fragment> : piece;
    });
}
